import type { RoomStatePayload } from '@completto/shared';
import { PlayerListView } from '../components/Lobby/PlayerList.ts';
import { RoomCodeDisplayView } from '../components/Lobby/RoomCodeDisplay.ts';
import { requestImmersiveLandscape } from '../lib/orientation-lock.ts';
import { router } from '../router.ts';
import type { MultiplayerService } from '../services/multiplayer.ts';

/**
 * S4 대기실 — 플레이어 리스트 + 호스트의 시작 버튼.
 */
export class WaitingRoomPage {
  private root: HTMLElement;
  private multiplayer: MultiplayerService;
  private unsubscribeRoomState: (() => void) | null = null;
  private unsubscribeError: (() => void) | null = null;
  private unsubscribeGameStarting: (() => void) | null = null;
  private state: RoomStatePayload | null;
  private code: string;

  constructor(root: HTMLElement, multiplayer: MultiplayerService, code: string) {
    this.root = root;
    this.multiplayer = multiplayer;
    this.code = code;
    this.state = multiplayer.getLastRoomState();
  }

  mount(): void {
    this.unsubscribeRoomState = this.multiplayer.onRoomState((s) => {
      // 진행/종료 상태로 입장한 경우 적절 페이지로 이동 (재입장 흐름)
      if (s.status === 'IN_PROGRESS') {
        router.navigate({ name: 'play', code: s.roomCode });
        return;
      }
      if (s.status === 'FINISHED') {
        router.navigate({ name: 'result', code: s.roomCode });
        return;
      }
      this.state = s;
      this.render();
    });
    this.unsubscribeError = this.multiplayer.onError((err) => {
      if (err.code === 'KICKED') {
        router.navigate({ name: 'error', reason: '룸에서 추방되었습니다' });
        return;
      }
      // 기타 에러 토스트로
      this.showFlash(`⚠ ${err.message}`);
    });
    this.unsubscribeGameStarting = this.multiplayer.onGameStarting((p) => {
      router.navigate({ name: 'play', code: p.roomCode });
    });

    // 룸 상태가 아직 없으면 (직접 URL 진입) 자동 입장 시도
    if (!this.state) {
      this.multiplayer.joinRoom(this.code);
    }

    this.render();
  }

  unmount(): void {
    this.unsubscribeRoomState?.();
    this.unsubscribeError?.();
    this.unsubscribeGameStarting?.();
  }

  private showFlash(text: string): void {
    const el = this.root.querySelector<HTMLElement>('[data-role="flash"]');
    if (el) {
      el.textContent = text;
      el.classList.remove('hidden');
      setTimeout(() => el.classList.add('hidden'), 3000);
    }
  }

  private render(): void {
    this.root.innerHTML = '';
    const main = document.createElement('main');
    main.className = 'min-h-screen safe-x safe-y flex flex-col items-center';
    main.style.setProperty('--page-pad-x', '1rem');
    main.style.setProperty('--page-pad-y', '1rem');
    const inner = document.createElement('div');
    inner.className = 'w-full max-w-md flex flex-col gap-4';
    main.appendChild(inner);

    if (!this.state) {
      inner.innerHTML = `
        <div class="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-wood-coffee/70">
          <div class="animate-pulse">룸 ${this.code} 입장 중…</div>
        </div>
      `;
      this.root.appendChild(main);
      return;
    }

    const headerRow = document.createElement('div');
    headerRow.className = 'flex items-center justify-between';
    headerRow.innerHTML = `
      <button data-role="leave" class="text-sm text-wood-coffee/70 hover:text-wood-ink">← 나가기</button>
      <span class="text-xs text-wood-coffee/55">대기실</span>
    `;
    inner.appendChild(headerRow);

    const publicOrigin = import.meta.env.VITE_PUBLIC_ORIGIN || window.location.origin;
    const shareUrl = `${publicOrigin}/room/${this.state.roomCode}`;
    const codeView = new RoomCodeDisplayView({
      code: this.state.roomCode,
      shareUrl,
    });
    inner.appendChild(codeView.render());

    const amHost = this.state.players.some((p) => p.id === this.state?.myPlayerId && p.isHost);

    const playersHeader = document.createElement('div');
    playersHeader.className = 'text-xs text-wood-coffee/70 mt-2';
    playersHeader.textContent = `── 플레이어 (${this.state.players.length}/4) ──`;
    inner.appendChild(playersHeader);

    const playerList = new PlayerListView({
      players: this.state.players,
      myPlayerId: this.state.myPlayerId,
      amHost,
      onKick: (id) => this.multiplayer.kickPlayer(id),
    });
    inner.appendChild(playerList.render());

    if (amHost) {
      const startBtn = document.createElement('button');
      startBtn.type = 'button';
      const canStart = this.state.players.filter((p) => p.connected).length >= 2;
      startBtn.disabled = !canStart;
      startBtn.textContent = canStart ? '게임 시작' : '게임 시작 (2명 이상 필요)';
      startBtn.className = `rounded-xl py-3 font-bold mt-3 transition ${
        canStart
          ? 'bg-card-navy hover:bg-card-deep text-card-gold'
          : 'bg-wood-sand/40 text-wood-coffee/55 cursor-not-allowed'
      }`;
      startBtn.addEventListener('click', () => {
        void requestImmersiveLandscape();
        this.multiplayer.startGame();
      });
      inner.appendChild(startBtn);
    } else {
      const wait = document.createElement('div');
      wait.className = 'rounded-xl bg-wood-paper text-center text-sm text-wood-coffee py-3 mt-3';
      wait.textContent = '호스트의 게임 시작을 기다리는 중…';
      inner.appendChild(wait);
    }

    const flash = document.createElement('div');
    flash.dataset.role = 'flash';
    flash.className = 'hidden text-rose-400 text-sm text-center mt-2';
    inner.appendChild(flash);

    this.root.appendChild(main);

    headerRow
      .querySelector<HTMLButtonElement>('[data-role="leave"]')
      ?.addEventListener('click', () => {
        this.multiplayer.leaveRoom();
        router.navigate({ name: 'main' });
      });
  }
}
